import { ref } from 'vue'
import { ADMIN_URL } from '../config/api'

const API_BASE = ADMIN_URL

function getAuthHeaders() {
  const token = localStorage.getItem('auth_token')
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

async function apiCall(url, options = {}) {
  const response = await fetch(url, {
    ...options,
    headers: { ...getAuthHeaders(), ...options.headers },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(error.error || error.detail || 'Request failed')
  }

  const result = await response.json()
  // Extract data from standardized response wrapper
  return result.data !== undefined ? result.data : result
}

export function useAdmin() {
  const loading = ref(false)
  const error = ref(null)

  // Stats
  const getStats = async () => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/stats`)
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  // Businesses
  const listBusinesses = async () => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses`)
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const getBusiness = async (id) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses/${id}`)
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  // Licenses
  const listLicenses = async (params = {}) => {
    loading.value = true
    error.value = null
    try {
      const queryString = new URLSearchParams(params).toString()
      const url = queryString ? `${API_BASE}/licenses?${queryString}` : `${API_BASE}/licenses`
      return await apiCall(url)
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const getLicense = async (id) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/licenses/${id}`)
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const createLicense = async (data) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/licenses`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const updateLicense = async (id, data, businessId) => {
    loading.value = true
    error.value = null
    try {
      const url = businessId
        ? `${API_BASE}/licenses/${id}?business_id=${businessId}`
        : `${API_BASE}/licenses/${id}`
      return await apiCall(url, {
        method: 'PUT',
        body: JSON.stringify(data),
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const deleteLicense = async (id) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/licenses/${id}`, {
        method: 'DELETE',
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const renewLicense = async (id, businessId, data) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/licenses/${id}/renew?business_id=${businessId}`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  // Payments
  const listPayments = async (params = {}) => {
    loading.value = true
    error.value = null
    try {
      const queryString = new URLSearchParams(params).toString()
      const url = queryString ? `${API_BASE}/payments?${queryString}` : `${API_BASE}/payments`
      return await apiCall(url)
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const recordPayment = async (data) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/payments`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const deletePayment = async (id) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/payments/${id}`, {
        method: 'DELETE',
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  // Business Management
  const createBusiness = async (data) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const updateBusiness = async (businessId, data) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses/${businessId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  // User Management
  const createUser = async (businessId, data) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses/${businessId}/users`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const listUsers = async (businessId) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses/${businessId}/users`)
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const updateUser = async (businessId, userId, data) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses/${businessId}/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  // Module Management
  const enableModule = async (businessId, moduleName) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses/${businessId}/modules`, {
        method: 'POST',
        body: JSON.stringify({ module_name: moduleName }),
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const disableModule = async (businessId, moduleName) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses/${businessId}/modules/${moduleName}`, {
        method: 'DELETE',
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const listModules = async (businessId) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses/${businessId}/modules`)
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  // List all available modules in the system (from registry)
  const listAvailableModules = async () => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/modules`)
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  // Music Library Management (ReelsGen)
  const listMusicTracks = async () => {
    loading.value = true
    error.value = null
    try {
      const result = await apiCall(`${API_BASE}/reelsgen/music`)
      return result.data || result
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const createMusicTrack = async (data) => {
    loading.value = true
    error.value = null
    try {
      const result = await apiCall(`${API_BASE}/reelsgen/music`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
      return result.data || result
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const updateMusicTrack = async (id, data) => {
    loading.value = true
    error.value = null
    try {
      const result = await apiCall(`${API_BASE}/reelsgen/music/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      })
      return result.data || result
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const deleteMusicTrack = async (id) => {
    loading.value = true
    error.value = null
    try {
      await apiCall(`${API_BASE}/reelsgen/music/${id}`, {
        method: 'DELETE',
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const importMusicFromURL = async (data) => {
    loading.value = true
    error.value = null
    try {
      const result = await apiCall(`${API_BASE}/reelsgen/music/import-url`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
      return result.data || result
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const uploadMusicFile = async (file) => {
    loading.value = true
    error.value = null
    try {
      const formData = new FormData()
      formData.append('file', file)

      const token = localStorage.getItem('auth_token')
      const response = await fetch(`${API_BASE}/reelsgen/music/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Upload failed' }))
        throw new Error(error.error || error.detail || 'Upload failed')
      }

      const result = await response.json()
      return result.data || result
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  // Collector Management
  const listCollectors = async (businessId) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses/${businessId}/collectors`)
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const createCollector = async (businessId, data) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses/${businessId}/collectors`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const resetCollectorPassword = async (businessId, collectorId, newPassword) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses/${businessId}/collectors/${collectorId}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ new_password: newPassword }),
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const resetUserPassword = async (businessId, userId, newPassword) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses/${businessId}/users/${userId}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ new_password: newPassword }),
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  // Module Settings (for module-specific configuration)
  const getSevaSettings = async (businessId) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses/${businessId}/modules/seva/settings`)
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const updateSevaSettings = async (businessId, data) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses/${businessId}/modules/seva/settings`, {
        method: 'PUT',
        body: JSON.stringify(data),
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    error,
    getStats,
    listBusinesses,
    getBusiness,
    createBusiness,
    updateBusiness,
    listLicenses,
    getLicense,
    createLicense,
    updateLicense,
    deleteLicense,
    renewLicense,
    listPayments,
    recordPayment,
    deletePayment,
    // User management
    createUser,
    listUsers,
    updateUser,
    resetUserPassword,
    // Module management
    enableModule,
    disableModule,
    listModules,
    listAvailableModules,
    // Music library management
    listMusicTracks,
    createMusicTrack,
    updateMusicTrack,
    deleteMusicTrack,
    importMusicFromURL,
    uploadMusicFile,
    // Collector management
    listCollectors,
    createCollector,
    resetCollectorPassword,
    // Module settings
    getSevaSettings,
    updateSevaSettings,
  }
}
